import { useState, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import { useParams, Link, useNavigate } from "react-router-dom";
import { getCanonicalUrl, getDefaultOgImage } from '@/lib/seoUtils';
import {
  ArrowLeft, BookOpen, Users, Clock, MapPin, Layers,
  GraduationCap, Target, Star, Tag, CalendarDays, BarChart2,
  ChevronRight, FlaskConical, RefreshCw, MessageCircle, Zap,
  Languages as LanguagesIcon, Award,
} from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { currencySymbol } from "@/lib/currency";
import { getPublicOfferingById } from "@/services/teacherProfileService";
import { getTeacherByHandle, getPublicOfferingByHandleAndSlug } from "@/services/marketplaceService";
import { savePendingIntent } from "@/utils/pendingIntent";
import { useAuth } from "@/hooks/use-auth-compat";
import { InquiryComposerModal } from "@/components/InquiryComposerModal";
import { useTeachingModes, useGradeBands, useCurricula, useAcceptedLevels, useLanguages } from "@/hooks/useValueCatalog";
import { getOfferingTypeTheme } from "@/lib/offeringTypeTheme";
import { migrateCrashCourseMilestones } from "@/lib/offeringMigration";

/**
 * Colour + icon come from the shared offering-type theme so this page always
 * matches search cards, the wizard, sidebar, and dashboard rails. `COURSE` is
 * a legacy alias for `CRASH_COURSE`, so it reuses that theme.
 */
const TYPE_LABELS: Record<string, string> = {
  ONE_TO_ONE: "1-on-1 Session",
  GROUP: "Group Class",
  CRASH_COURSE: "Crash Course",
  COURSE: "Crash Course",
};

function buildTypeConfig(offeringType: string) {
  const key = offeringType === "COURSE" ? "CRASH_COURSE" : offeringType;
  const theme = getOfferingTypeTheme(key);
  return {
    label: TYPE_LABELS[offeringType] ?? theme.typeLabel,
    icon: theme.icon,
    gradient: theme.gradient,
    accent: theme.soft,
    accentText: theme.accent,
    accentBorder: theme.border,
  };
}

const DAY_SHORT: Record<string, string> = {
  MONDAY: "Mon", TUESDAY: "Tue", WEDNESDAY: "Wed",
  THURSDAY: "Thu", FRIDAY: "Fri", SATURDAY: "Sat", SUNDAY: "Sun",
};

// Experience bucket codes → human labels; rendered as "<label> of experience".
const EXPERIENCE_LABELS: Record<string, string> = {
  LESS_THAN_1: "Less than 1 year",
  ONE_TO_3: "1–3 years",
  THREE_TO_5: "3–5 years",
  FIVE_TO_10: "5–10 years",
  MORE_THAN_10: "More than 10 years",
};

const fmtTime = (t: string) => {
  const [hh, mm] = t.split(":").map(Number);
  const ampm = hh < 12 ? "AM" : "PM";
  const dh = hh % 12 === 0 ? 12 : hh % 12;
  return `${dh}:${String(mm).padStart(2, "0")} ${ampm}`;
};

const fmtDate = (d?: string | null) => {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return d; }
};

function HeroPill({ icon: Icon, children }: { icon?: any; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full bg-white/15 text-white">
      {Icon && <Icon className="w-3.5 h-3.5 opacity-80" />}
      {children}
    </span>
  );
}

function Section({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <div>
      {title && (
        <div className="flex items-center gap-3 mb-4">
          <div className="h-5 w-1 rounded-full bg-gradient-to-b from-slate-400 to-slate-500" />
          <h3 className="text-xl font-bold text-foreground">{title}</h3>
        </div>
      )}
      {children}
    </div>
  );
}

export default function PublicOfferingPage() {
  // Handles two routes:
  //   /public/offering/:id   — legacy UUID route (backward compat)
  //   /t/:handle/:slug       — new SEO-friendly route
  const { id, handle, slug } = useParams<{ id?: string; handle?: string; slug?: string }>();
  const navigate = useNavigate();
  const auth = useAuth();

  const [offering, setOffering] = useState<any>(null);
  const [teacher, setTeacher] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);

  const teachingModes = useTeachingModes();
  const gradeBands = useGradeBands();
  const curricula = useCurricula();
  const acceptedLevels = useAcceptedLevels();
  const languages = useLanguages();

  useEffect(() => {
    setLoading(true);
    setOffering(null);
    setNotFound(false);

    if (handle && slug) {
      // New route: /t/:handle/:slug
      getPublicOfferingByHandleAndSlug(handle, slug)
        .then(data => { if (!data) { setNotFound(true); return; } setOffering(data); })
        .catch(() => setNotFound(true))
        .finally(() => setLoading(false));
    } else if (id) {
      // Legacy route: /public/offering/:id — resolve then redirect to slug URL
      getPublicOfferingById(id)
        .then(data => {
          if (!data) { setNotFound(true); return; }
          setOffering(data);
          if (data.teacherHandle && data.slug) {
            navigate(`/t/${data.teacherHandle}/${data.slug}`, { replace: true });
          }
        })
        .catch(() => setNotFound(true))
        .finally(() => setLoading(false));
    } else {
      setNotFound(true);
      setLoading(false);
    }
  }, [id, handle, slug]);

  useEffect(() => {
    const handle = offering?.teacherHandle;
    if (!handle) return;
    getTeacherByHandle(handle).then(setTeacher).catch(() => {});
  }, [offering?.teacherHandle]);

  const handleContact = () => {
    const handle = offering?.teacherHandle;
    if (!auth.isAuthenticated) {
      const raw = sessionStorage.getItem("bmc:search-context");
      const ctx = raw ? JSON.parse(raw) : null;
      localStorage.setItem("bmc:anonymous-search-context", JSON.stringify({
        teacherHandle: handle, intendedOfferingId: id ?? null,
        searchQuery: ctx?.searchQuery ?? null, activeFilters: ctx?.activeFilters ?? null,
      }));
      window.dispatchEvent(new CustomEvent("bmc:open-registration-gate", { detail: { source: "public-offering" } }));
      return;
    }
    if (handle) savePendingIntent({ type: "INQUIRY", teacherHandle: handle });
    setInquiryOpen(true);
  };

  if (loading) return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Loading... | BookMyCoaching</title>
        <meta name="description" content="BookMyCoaching — find verified tutors and coaches across India." />
      </Helmet>
      <Header />
      <main className="pt-28 flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </main>
      <Footer />
    </div>
  );

  if (notFound || !offering) return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Offering Not Found | BookMyCoaching</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Header />
      <main className="pt-28 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <p className="text-lg font-medium text-foreground mb-2">Offering not found</p>
          <p className="text-sm text-muted-foreground mb-6">This offering may not be available.</p>
          <Button variant="outline" onClick={() => history.back()}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );

  const offeringType = (offering.offeringType ?? offering.type ?? "ONE_TO_ONE").toUpperCase();
  const cfg = buildTypeConfig(offeringType);
  const Icon = cfg.icon;
  const teacherHandle: string | null = offering.teacherHandle || null;

  const plans: any[] = offering.pricingPlans ?? [];
  const primaryPlan = plans[0] ?? null;
  const displayPrice: number | null = primaryPlan
    ? primaryPlan.regularPrice
    : (offering.displayPrice != null ? Math.round(offering.displayPrice / 100) : null);
  // SCRUM-365: backend currency only — no 'USD' fallback (missing = no symbol).
  const displayCurrency: string | null = primaryPlan?.currency ?? offering.displayCurrency ?? null;
  const sym = currencySymbol(displayCurrency);
  const trialPrice = primaryPlan?.trialSessionPrice ?? null;
  const earlyBirdPrice = primaryPlan?.earlyBirdPrice ?? null;
  // When the primary plan's early-bird price beats the regular price, headline
  // the discounted number and strike through the regular one.
  const earlyBirdDisplay =
    displayPrice != null && earlyBirdPrice != null && earlyBirdPrice < displayPrice
      ? earlyBirdPrice
      : null;
  const earlyBirdSavingsPct =
    earlyBirdDisplay != null && displayPrice != null
      ? Math.round((1 - earlyBirdDisplay / displayPrice) * 100)
      : null;

  // GROUP/CRASH_COURSE are priced per batch/course but browsed per-session (the card
  // shows a "/session" figure). Headline the per-session price here too, with the batch
  // total shown beneath it. Prefer the plan's per-session equivalent; fall back to
  // dividing the total by the planned session count.
  const isPerSessionType = offeringType === "GROUP" || offeringType === "CRASH_COURSE";
  const totalSessions: number | null =
    offeringType === "GROUP" ? (offering.group?.totalSessionsPlanned ?? null)
      : offeringType === "CRASH_COURSE" ? (offering.crashCourse?.totalSessions ?? null)
        : null;
  const perSessionRegular: number | null = isPerSessionType
    ? (primaryPlan?.perSessionEquivalent
      ?? (displayPrice != null && totalSessions ? displayPrice / totalSessions : null))
    : null;
  const perSessionEarly: number | null = isPerSessionType
    ? (primaryPlan?.earlyBirdPerSessionEquivalent
      ?? (earlyBirdDisplay != null && totalSessions ? earlyBirdDisplay / totalSessions : null))
    : null;
  const showPerSession = isPerSessionType && perSessionRegular != null;
  // Whole-unit money: integers get thousands separators; fractional per-session
  // figures (total ÷ sessions) keep up to 2 decimals.
  const fmtMoney = (n: number): string =>
    Number.isInteger(n)
      ? n.toLocaleString()
      : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  /* collect which left sections exist */
  const hasAbout      = !!(offering.description || offering.topicNames?.length);
  const hasLearn      = !!(offering.learningOutcomes || offering.prerequisites || offering.targetScorePromise);
  const hasSession    = !!(offering.oneToOne || offering.group || offering.crashCourse);
  const hasCurriculum = !!(offering.curriculumSystems?.length || offering.acceptedLevels?.length
    || offering.instructionLanguages?.length || offering.targetExamNames?.length);
  const hasAvail      = !!(offering.availabilitySlots?.length);

  const chipMuted = "inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full bg-muted text-foreground border border-border";

  const offeringOgImage = teacher?.profileImage ?? getDefaultOgImage();
  const offeringTitle = offering?.title && teacher?.fullName
    ? `${offering.title} by ${teacher.fullName} | BookMyCoaching`
    : 'Offering | BookMyCoaching';
  const offeringDescription = `${offering?.title ?? 'Coaching offering'} — view details and send an inquiry on BookMyCoaching.`;
  const offeringCanonicalUrl = getCanonicalUrl(
    offering?.teacherHandle && offering?.slug
      ? `/t/${offering.teacherHandle}/${offering.slug}`
      : `/public/offering/${id ?? ''}`
  );

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{offeringTitle}</title>
        <meta name="description" content={offeringDescription} />
        <link rel="canonical" href={offeringCanonicalUrl} />
        <meta property="og:title" content={offeringTitle} />
        <meta property="og:description" content={offeringDescription} />
        <meta property="og:url" content={offeringCanonicalUrl} />
        <meta property="og:image" content={offeringOgImage} />
      </Helmet>
      <Header />

      <main className="pt-28 pb-16">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">

          {/* Back — follows natural browser history; users typically arrive from search */}
          <div className="mb-4">
            <button
              type="button"
              onClick={() => history.back()}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>

          {/* Hero */}
          <div className={cn("bg-gradient-to-br rounded-2xl text-white px-6 py-8 mb-8 shadow-md", cfg.gradient)}>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest bg-white/20 px-3 py-1 rounded-lg">
                <Icon className="w-3.5 h-3.5" /> {cfg.label}
              </span>
              {offering.trialSessionAvailable && (
                <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-amber-400/20 text-amber-200 border border-amber-300/30 px-3 py-1 rounded-lg">
                  <Zap className="w-3 h-3" /> Free Trial
                </span>
              )}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight mb-1">{offering.title}</h1>
            {offering.subjectName && <p className="text-sm text-white/60 mb-3">{offering.subjectName}</p>}
            <div className="flex flex-wrap gap-2 mt-2">
              {offering.teachingMode && (
                <HeroPill icon={MapPin}>{teachingModes.labelOf(offering.teachingMode)}</HeroPill>
              )}
              {(offering.oneToOne?.sessionDurationMinutes ?? offering.group?.sessionDurationMinutes) && (
                <HeroPill icon={Clock}>{offering.oneToOne?.sessionDurationMinutes ?? offering.group?.sessionDurationMinutes} min / session</HeroPill>
              )}
              {offering.group?.sessionsPerWeek && <HeroPill icon={BarChart2}>{offering.group.sessionsPerWeek}× per week</HeroPill>}
              {offering.crashCourse?.totalSessions && <HeroPill icon={BarChart2}>{offering.crashCourse.totalSessions} sessions</HeroPill>}
              {offering.gradeBands?.length > 0 && (
                <HeroPill icon={GraduationCap}>{offering.gradeBands.map((g: string) => gradeBands.labelOf(g)).join(", ")}</HeroPill>
              )}
              {offering.group?.batchSizeMin != null && offering.group?.batchSizeMax != null && (
                <HeroPill icon={Users}>Batch: {offering.group.batchSizeMin}–{offering.group.batchSizeMax}</HeroPill>
              )}
            </div>
          </div>

          {/* Two-column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8 items-start">

            {/* Left — content sections, no card wrapper */}
            <div className="space-y-6">

              {/* About + Topics */}
              {hasAbout && (
                <Section>
                  {offering.description && (
                    <>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="h-5 w-1 rounded-full bg-gradient-to-b from-slate-400 to-slate-500" />
                        <h3 className="text-xl font-bold text-foreground">About this Offering</h3>
                      </div>
                      <div
                        className="text-[15px] text-foreground/80 leading-relaxed prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: offering.description }}
                      />
                    </>
                  )}
                  {offering.topicNames?.length > 0 && (
                    <div className={offering.description ? "mt-5 pt-5 border-t border-border" : ""}>
                      <h3 className="text-base font-semibold text-foreground mb-3">Topics Covered</h3>
                      <div className="flex flex-wrap gap-2">
                        {offering.topicNames.map((t: string) => (
                          <span key={t} className={cn("inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full border", cfg.accent, cfg.accentText, cfg.accentBorder)}>
                            <Tag className="w-3 h-3" />{t}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </Section>
              )}

              {/* What You'll Learn */}
              {hasLearn && (
                <Section title="What You'll Learn">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {offering.learningOutcomes && (
                      <div className="rounded-xl bg-[hsl(39,96%,48%)]/8 border border-[hsl(39,96%,48%)]/20 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-7 h-7 rounded-lg bg-[hsl(39,96%,48%)]/15 flex items-center justify-center shrink-0">
                            <Target className="w-3.5 h-3.5 text-[hsl(39,72%,38%)]" />
                          </div>
                          <p className="text-sm font-semibold text-[hsl(39,60%,30%)]">Outcomes</p>
                        </div>
                        <div className="text-sm text-foreground/80 leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: offering.learningOutcomes }} />
                      </div>
                    )}
                    {offering.prerequisites && (
                      <div className="rounded-xl bg-[#EEEFF9] border border-[#DCDDEE] p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-7 h-7 rounded-lg bg-slate-200 flex items-center justify-center shrink-0">
                            <Layers className="w-3.5 h-3.5 text-[#5A5E80]" />
                          </div>
                          <p className="text-sm font-semibold text-[#262A7A]">Prerequisites</p>
                        </div>
                        <div className="text-sm text-foreground/80 leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: offering.prerequisites }} />
                      </div>
                    )}
                    {offering.targetScorePromise && (
                      <div className="rounded-xl bg-[hsl(237,52%,31%)]/8 border border-[hsl(237,52%,31%)]/15 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-7 h-7 rounded-lg bg-[hsl(237,52%,31%)]/15 flex items-center justify-center shrink-0">
                            <Star className="w-3.5 h-3.5 text-[hsl(237,52%,31%)]" />
                          </div>
                          <p className="text-sm font-semibold text-[hsl(237,52%,25%)]">Score Promise</p>
                        </div>
                        <div className="text-sm text-foreground/80 leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: offering.targetScorePromise }} />
                      </div>
                    )}
                  </div>
                </Section>
              )}

              {/* Session / Batch / Course details */}
              {hasSession && (
                <Section title={offering.group ? "Batch Details" : offering.crashCourse ? "Course Details" : "Session Details"}>

                  {/* 1-on-1 */}
                  {offering.oneToOne?.sessionDurationMinutes && (
                    <div className="flex items-center gap-3 bg-muted/50 rounded-xl px-4 py-3 w-fit">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Duration</p>
                        <p className="text-sm font-semibold text-foreground">{offering.oneToOne.sessionDurationMinutes} minutes per session</p>
                      </div>
                    </div>
                  )}

                  {/* Group */}
                  {offering.group && (
                    <>
                      <div className="flex flex-wrap gap-2 mb-5">
                        {offering.group.batchSizeMin != null && offering.group.batchSizeMax != null && (
                          <span className={cn("inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border font-medium", cfg.accent, cfg.accentText, cfg.accentBorder)}>
                            <Users className="w-3.5 h-3.5 shrink-0" />
                            {offering.group.batchSizeMin}–{offering.group.batchSizeMax} students
                          </span>
                        )}
                        {offering.group.sessionsPerWeek != null && (
                          <span className={chipMuted}><BarChart2 className="w-3.5 h-3.5 shrink-0" /><b>{offering.group.sessionsPerWeek}</b> sessions/week</span>
                        )}
                        {offering.group.sessionDurationMinutes != null && (
                          <span className={chipMuted}><Clock className="w-3.5 h-3.5 shrink-0" /><b>{offering.group.sessionDurationMinutes}</b> min</span>
                        )}
                        {offering.group.totalSessionsPlanned != null && (
                          <span className={chipMuted}><BookOpen className="w-3.5 h-3.5 shrink-0" /><b>{offering.group.totalSessionsPlanned}</b> total sessions</span>
                        )}
                        {offering.group.nextBatchStartDate && (
                          <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-[hsl(237,52%,31%)]/8 text-[hsl(237,52%,31%)] border border-[hsl(237,52%,31%)]/15">
                            <CalendarDays className="w-3.5 h-3.5 shrink-0" />
                            Next batch: <b>{fmtDate(offering.group.nextBatchStartDate)}</b>
                          </span>
                        )}
                        {offering.group.rollingEnrollment && (
                          <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-[hsl(237,52%,31%)]/8 text-[hsl(237,52%,31%)] border border-[hsl(237,52%,31%)]/15 font-semibold">
                            Rolling Enrollment{offering.group.rollingEnrollmentCutoffWeek ? ` · until wk ${offering.group.rollingEnrollmentCutoffWeek}` : ""}
                          </span>
                        )}
                        {offering.computedFields?.seatsRemaining != null && (
                          <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 font-semibold">
                            {offering.computedFields.seatsRemaining} seat{offering.computedFields.seatsRemaining !== 1 ? "s" : ""} left
                          </span>
                        )}
                      </div>

                      {/* Milestones */}
                      {(offering.group.curriculumMilestones ?? offering.group.milestones)?.length > 0 && (
                        <>
                          <p className="text-sm font-semibold text-foreground mb-3">Curriculum Milestones</p>
                          <div className="space-y-0">
                            {(offering.group.curriculumMilestones ?? offering.group.milestones).map((m: any, i: number) => {
                              const assessmentLabel = m.assessmentType && m.assessmentType !== 'NONE'
                                ? ({ QUIZ: 'Quiz', ASSIGNMENT: 'Assignment', MOCK_TEST: 'Mock Test', PROJECT: 'Project' }[m.assessmentType as string] ?? null)
                                : m.hasAssessment ? 'Assessment' : null;
                              return (
                                <div key={i} className="py-3 border-b border-border/60 last:border-0">
                                  <div className="flex items-start gap-3">
                                    <span className={cn("text-xs font-extrabold px-2 py-1 rounded-lg shrink-0 mt-0.5", cfg.accent, cfg.accentText)}>W{m.weekNumber}</span>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-medium text-foreground">{m.title}</span>
                                        {assessmentLabel && (
                                          <span className="text-xs px-2 py-0.5 rounded-full bg-[hsl(39,96%,48%)]/12 text-[hsl(39,72%,38%)] font-semibold border border-[hsl(39,96%,48%)]/25 shrink-0">{assessmentLabel}</span>
                                        )}
                                      </div>
                                      {m.description && (
                                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{m.description}</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {/* Crash course */}
                  {offering.crashCourse && (
                    <>
                      {(offering.crashCourse.courseStartDate || offering.crashCourse.courseEndDate) && (
                        <div className="flex items-center gap-3 bg-muted/50 rounded-xl px-4 py-3 mb-4 w-fit">
                          <CalendarDays className="w-4 h-4 text-muted-foreground shrink-0" />
                          <div>
                            <p className="text-xs text-muted-foreground">Course Dates</p>
                            <p className="text-sm font-semibold text-foreground">
                              {fmtDate(offering.crashCourse.courseStartDate)}{offering.crashCourse.courseEndDate ? ` → ${fmtDate(offering.crashCourse.courseEndDate)}` : ""}
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 mb-5">
                        {offering.crashCourse.totalSessions && <span className={chipMuted}><Layers className="w-3.5 h-3.5 shrink-0" /><b>{offering.crashCourse.totalSessions}</b> sessions</span>}
                        {offering.crashCourse.sessionsPerWeek && <span className={chipMuted}><Clock className="w-3.5 h-3.5 shrink-0" /><b>{offering.crashCourse.sessionsPerWeek}×</b> per week</span>}
                        {offering.crashCourse.seatsTotal && (
                          <span className={chipMuted}>
                            <Users className="w-3.5 h-3.5 shrink-0" />
                            <b>{offering.crashCourse.seatsTotal}</b> seats
                            {offering.computedFields?.seatsRemaining != null && <span className="text-muted-foreground">({offering.computedFields.seatsRemaining} left)</span>}
                          </span>
                        )}
                        {offering.crashCourse.minimumEntryLevel && (
                          <span className={chipMuted}><GraduationCap className="w-3.5 h-3.5 shrink-0" /><b className="capitalize">{offering.crashCourse.minimumEntryLevel.replace(/_/g, " ").toLowerCase()}</b></span>
                        )}
                        {offering.crashCourse.mockTestsIncluded != null && offering.crashCourse.mockTestsIncluded > 0 && (
                          <span className={chipMuted}><FlaskConical className="w-3.5 h-3.5 shrink-0" /><b>{offering.crashCourse.mockTestsIncluded}</b> mock test{offering.crashCourse.mockTestsIncluded !== 1 ? "s" : ""}</span>
                        )}
                        {offering.crashCourse.doubtClearingSessionsPerWeek != null && offering.crashCourse.doubtClearingSessionsPerWeek > 0 && (
                          <span className={chipMuted}><MessageCircle className="w-3.5 h-3.5 shrink-0" /><b>{offering.crashCourse.doubtClearingSessionsPerWeek}×</b> doubt-clearing/week</span>
                        )}
                        {offering.crashCourse.studyMaterialIncluded && (
                          <span className={chipMuted}><BookOpen className="w-3.5 h-3.5 shrink-0" /> Study material included</span>
                        )}
                      </div>

                      {/* Week plan */}
                      {(() => {
                        // Read new milestones or migrate legacy weekPlan
                        const milestones = migrateCrashCourseMilestones(offering.crashCourse);
                        if (!milestones.length) return null;
                        const assessmentLabel = (type: string) =>
                          ({ QUIZ: 'Quiz', ASSIGNMENT: 'Assignment', MOCK_TEST: 'Mock Test', PROJECT: 'Project' }[type] ?? null);
                        return (
                          <>
                            <p className="text-sm font-semibold text-foreground mb-3">Curriculum Milestones</p>
                            <div className="space-y-0">
                              {milestones.map((m: any, i: number) => {
                                const badge = m.assessmentType && m.assessmentType !== 'NONE'
                                  ? assessmentLabel(m.assessmentType)
                                  : m.hasAssessment ? 'Assessment' : null;
                                return (
                                  <div key={i} className="py-3 border-b border-border/60 last:border-0">
                                    <div className="flex items-start gap-3">
                                      <span className={cn("text-xs font-extrabold px-2 py-1 rounded-lg shrink-0 mt-0.5", cfg.accent, cfg.accentText)}>W{m.weekNumber}</span>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="text-sm font-medium text-foreground">{m.title || m.theme}</span>
                                          {badge && (
                                            <span className="text-xs px-2 py-0.5 rounded-full bg-[hsl(39,96%,48%)]/12 text-[hsl(39,72%,38%)] font-semibold border border-[hsl(39,96%,48%)]/25 shrink-0">{badge}</span>
                                          )}
                                        </div>
                                        {m.description && (
                                          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{m.description}</p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        );
                      })()}

                      {offering.crashCourse.previousBatchResults && (
                        <div className="mt-5 rounded-xl bg-[hsl(39,96%,48%)]/8 border border-[hsl(39,96%,48%)]/20 p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Star className="w-3.5 h-3.5 text-[hsl(39,72%,38%)] shrink-0" />
                            <p className="text-sm font-semibold text-[hsl(39,60%,30%)]">Previous Batch Results</p>
                          </div>
                          <div className="text-sm text-foreground/80 leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: offering.crashCourse.previousBatchResults }} />
                        </div>
                      )}
                    </>
                  )}
                </Section>
              )}

              {/* Curriculum, Levels, Languages & Exams */}
              {hasCurriculum && (
                <Section title="Curriculum, Levels & Exams">
                  <div className="space-y-4">
                    {offering.curriculumSystems?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Curriculum Systems</p>
                        <div className="flex flex-wrap gap-2">
                          {offering.curriculumSystems.map((c: string) => (
                            <span key={c} className="text-sm font-medium px-3 py-1 rounded-full bg-muted text-foreground border border-border">{curricula.labelOf(c)}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {offering.acceptedLevels?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Accepted Levels</p>
                        <div className="flex flex-wrap gap-2">
                          {offering.acceptedLevels.map((l: string) => (
                            <span key={l} className="text-sm font-medium px-3 py-1 rounded-full bg-muted text-foreground border border-border">{acceptedLevels.labelOf(l)}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {offering.instructionLanguages?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Languages of Instruction</p>
                        <div className="flex flex-wrap gap-2">
                          {offering.instructionLanguages.map((l: string) => (
                            <span key={l} className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1 rounded-full bg-muted text-foreground border border-border">
                              <LanguagesIcon className="w-3.5 h-3.5 shrink-0" />{languages.labelOf(l)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {offering.targetExamNames?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Target Exams</p>
                        <div className="flex flex-wrap gap-2">
                          {offering.targetExamNames.map((e: string) => (
                            <span key={e} className={cn("inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1 rounded-full border", cfg.accent, cfg.accentText, cfg.accentBorder)}>
                              <Award className="w-3.5 h-3.5 shrink-0" />{e}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </Section>
              )}

              {/* Availability */}
              {hasAvail && (
                <Section title="Availability">
                  <div className="divide-y divide-border/60">
                    {offering.availabilitySlots.map((s: any, i: number) => (
                      <div key={i} className="flex items-center justify-between py-2.5">
                        <span className={cn("text-sm font-semibold", cfg.accentText)}>
                          {DAY_SHORT[s.dayOfWeek] || s.dayOfWeek?.slice(0, 3)}
                        </span>
                        <span className="text-sm text-foreground">
                          {s.timeFrom ? fmtTime(s.timeFrom.slice(0, 5)) : ""}
                          {s.timeTo ? ` – ${fmtTime(s.timeTo.slice(0, 5))}` : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

            </div>

            {/* Right sidebar */}
            <div className="space-y-4 lg:sticky lg:top-24">

              {/* Price + CTA — one card */}
              <div className="rounded-2xl bg-[#EEEFF9] dark:bg-slate-800/30 shadow-sm overflow-hidden">

                {/* Price */}
                <div className="px-5 pt-5 pb-4">
                  {displayPrice != null ? (
                    showPerSession ? (
                      <>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">Starting from</p>
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <p className="text-4xl font-extrabold text-foreground leading-none">{sym}{fmtMoney(perSessionEarly ?? perSessionRegular!)}</p>
                          {perSessionEarly != null && (
                            <p className="text-lg font-medium text-muted-foreground line-through leading-none">{sym}{fmtMoney(perSessionRegular!)}</p>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          per session{earlyBirdSavingsPct != null ? ` · early bird, save ${earlyBirdSavingsPct}%` : ""}
                        </p>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {sym}{(earlyBirdDisplay ?? displayPrice).toLocaleString()} total{totalSessions ? ` · ${totalSessions} sessions` : ""}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">Starting from</p>
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <p className="text-4xl font-extrabold text-foreground leading-none">{sym}{(earlyBirdDisplay ?? displayPrice).toLocaleString()}</p>
                          {earlyBirdDisplay != null && (
                            <p className="text-lg font-medium text-muted-foreground line-through leading-none">{sym}{displayPrice.toLocaleString()}</p>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {offeringType === "ONE_TO_ONE" ? "per session" : "total"}{earlyBirdSavingsPct != null ? ` · early bird, save ${earlyBirdSavingsPct}%` : ""}
                        </p>
                      </>
                    )
                  ) : (
                    <p className="text-base text-muted-foreground">Contact for pricing</p>
                  )}
                </div>

                {/* Per-currency breakdown */}
                {plans.length > 1 && (
                  <div className="px-5 pb-3 space-y-1.5">
                    {plans.map((p: any) => {
                      const s = currencySymbol(p.currency);
                      return (
                        <div key={p.currency} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                          <span className="font-semibold text-foreground">{s}{p.regularPrice}</span>
                          {p.earlyBirdPrice && <span className="text-xs text-[hsl(39,72%,38%)] bg-[hsl(39,96%,48%)]/10 border border-[hsl(39,96%,48%)]/20 rounded-full px-2 py-0.5">Early bird {s}{p.earlyBirdPrice}</span>}
                          {p.trialSessionPrice && <span className="text-xs text-[hsl(237,52%,31%)] bg-[hsl(237,52%,31%)]/8 border border-[hsl(237,52%,31%)]/15 rounded-full px-2 py-0.5">Trial {s}{p.trialSessionPrice}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Trial + early bird inline badges */}
                {(offering.trialSessionAvailable || offering.earlyBirdDeadline || earlyBirdPrice) && (
                  <div className="px-5 pb-3 flex flex-wrap gap-2">
                    {offering.trialSessionAvailable && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[hsl(237,52%,31%)]/8 text-[hsl(237,52%,31%)] border border-[hsl(237,52%,31%)]/15">
                        <Zap className="w-3 h-3" />
                        {trialPrice ? `Trial ${sym}${trialPrice}` : "Free trial available"}
                      </span>
                    )}
                    {(offering.earlyBirdDeadline || earlyBirdPrice) && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-[hsl(39,96%,48%)]/10 text-[hsl(39,72%,38%)] border border-[hsl(39,96%,48%)]/20">
                        <CalendarDays className="w-3 h-3" />
                        {offering.earlyBirdDeadline ? `Early bird until ${fmtDate(offering.earlyBirdDeadline)}` : "Early bird pricing"}
                      </span>
                    )}
                  </div>
                )}

                {/* CTAs */}
                <div className="px-5 pb-5 space-y-2">
                  <Button className="w-full gap-2" size="lg" onClick={handleContact}>
                    <MessageCircle className="w-4 h-4" /> Contact Teacher
                  </Button>
                </div>
              </div>

              {/* Teacher mini-card */}
              {teacher && (
                <Link to={`/t/${teacherHandle}`} className="block rounded-2xl bg-[#EEEFF9] dark:bg-slate-800/30 px-5 py-4 hover:shadow-md transition-shadow">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-3">About the Teacher</p>
                  <div className="flex items-center gap-3">
                    {teacher.profileImage ? (
                      <img src={teacher.profileImage} alt={teacher.fullName} className="w-11 h-11 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="w-11 h-11 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <span className="text-base font-bold text-muted-foreground">{teacher.fullName?.charAt(0)}</span>
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground leading-snug">{teacher.fullName}</p>
                      {teacher.yearsTeachingExperience && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {EXPERIENCE_LABELS[teacher.yearsTeachingExperience] ?? teacher.yearsTeachingExperience} of experience
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto shrink-0" />
                  </div>
                </Link>
              )}
            </div>

          </div>
        </div>
      </main>

      <Footer />

      {teacher?.teacherUserId && (
        <InquiryComposerModal
          teacherUserId={teacher.teacherUserId}
          teacherName={teacher.fullName}
          teacherHandle={teacherHandle ?? ""}
          offeringId={offering.id}
          open={inquiryOpen}
          onClose={() => setInquiryOpen(false)}
        />
      )}
    </div>
  );
}
